import { Button, Flex, Modal, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { PageContainer, PageHeader } from '../../../shared_components';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import { QrPopup } from './QrPopup';
import QRCodeStyling from 'qr-code-styling';
import { useEffect, useRef, useState } from 'react';
import { OrderDataStoreContext } from '../stores';
import { useStore } from '../../../shared_hooks';
import { QrError } from './QrError';
import { intervalTimeInMs } from '../constants';
import classes from '../../../app.module.css';
import { Link, useNavigate } from 'react-router-dom';
import { useUserDataStore } from '../../../shared_stores';

const qrSideLength = Math.min(window.innerWidth, 500) - 48;

const qrCode = new QRCodeStyling({
  width: qrSideLength,
  height: qrSideLength,
  type: 'svg',
  margin: 0,
  qrOptions: {
    errorCorrectionLevel: 'M',
  },
  backgroundOptions: {
    color: '#00000000',
  },
  dotsOptions: {
    color: '#192D50',
    type: 'square',
  },
  cornersSquareOptions: {
    color: '#192D50',
    type: 'dot',
  },
  cornersDotOptions: {
    color: '#192D50',
    type: 'dot',
  },
});

export const StepQr = observer(() => {
  const { t } = useTranslation('payment');
  const navigate = useNavigate();
  const orderDataStore = useStore(OrderDataStoreContext);
  const userDataStore = useUserDataStore();
  const [opened, { open, close }] = useDisclosure(false);
  const ref = useRef(null);
  const [qrError, setQrError] = useState(false);
  const qrToken = orderDataStore.qrToken;
  const deviceId = localStorage.getItem('device_id');
  const [displayAvailabilityInfoButton, setDisplayAvailabilityInfoButton] =
    useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);

  useEffect(() => {
    void orderDataStore.loadQrToken();
  }, [orderDataStore]);

  useEffect(() => {
    if (qrToken?.includes('error')) {
      setQrError(true);
    } else {
      if (ref.current) {
        qrCode.update({
          data: qrToken,
        });
        qrCode.append(ref.current);
      }
    }
  }, [qrToken]);

  useEffect(() => {
    const interval = setInterval(() => {
      void orderDataStore.checkDeviceAvailability(deviceId || '');
    }, intervalTimeInMs);
    return () => clearInterval(interval);
  });

  useEffect(() => {
    if (
      !orderDataStore.deviceAvailable ||
      orderDataStore.errorCheckingDeviceAvailability
    ) {
      setDisplayAvailabilityInfoButton(true);
    }
  }, [
    orderDataStore.deviceAvailable,
    orderDataStore.errorCheckingDeviceAvailability,
  ]);

  useEffect(() => {
    const orderInterval = setInterval(() => {
      if (qrToken) {
        void userDataStore.checkOrderStatus(qrToken.split('token=')[1]);
      }
    }, intervalTimeInMs);
    return () => clearInterval(orderInterval);
  });

  useEffect(() => {
    if (userDataStore.measurementComplete && qrToken) {
      userDataStore.measurementComplete = false;
      void navigate(`/reports?token=${qrToken.split('token=')[1]}`);
    }
  }, [userDataStore.measurementComplete, userDataStore, navigate, qrToken]);

  if (qrError) {
    return <QrError />;
  }

  return (
    <PageContainer darkMode>
      <Modal
        variant={'dark'}
        style={{ color: 'var(--mantine-color-clinicalWhite-0)' }}
        opened={opened}
        onClose={close}
        title={t('qr_code_info_title')}
      >
        <QrPopup />
      </Modal>
      <Modal
        style={{ color: 'var(--mantine-color-clinicalWhite-0)' }}
        classNames={{ content: classes.modalContentWhite }}
        opened={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        title={t('payment:availability_modal_title')}
      >
        <Stack>
          <Text>
            {t('payment:availability_modal_message')}
            <Link
              to={'/'}
              style={{
                textDecoration: 'underline',
              }}
            >
              {t('support_team')}
            </Link>
            .
          </Text>
          <Button onClick={() => setShowAvailabilityModal(false)}>
            {t('common:understood')}
          </Button>
        </Stack>
      </Modal>
      <Flex direction={'column'} justify={'space-between'} h={'100%'}>
        <Stack>
          <PageHeader
            title={t('qr_title')}
            message={t('qr_message')}
            darkMode
          />

          <Flex
            style={{
              aspectRatio: '1/1',
              backgroundColor: 'var(--mantine-color-clinicalWhite-0)',
              borderRadius: 'var(--mantine-radius-lg)',
            }}
          >
            <div ref={ref} style={{ margin: 'auto' }}></div>
          </Flex>
          {displayAvailabilityInfoButton && (
            <Button
              onClick={() => setShowAvailabilityModal(true)}
              variant="white-text"
            >
              {t('payment:device_availability_help')}
            </Button>
          )}
        </Stack>
        <Button
          onClick={open}
          variant="white-text"
          leftSection={<HelpOutlineIcon />}
        >
          {t('qr_help')}
        </Button>
      </Flex>
    </PageContainer>
  );
});
